/**
 * DOG OWNED — surfacing the saved dog outside the guided journey.
 *
 * A quiet "Shopping for Alfie" on the homepage, the collections index and retail
 * collections. Enhancement only: the server-rendered invitation already works
 * without this file, and with no JavaScript there is no saved dog to surface
 * anyway — the profile lives on the device and never reaches Liquid.
 *
 * This file owns no parsing. dog-profile.js is the only thing that reads,
 * validates or writes the profile; a second parser would be a second set of
 * rules about what counts as valid, and they would drift. If DogProfile is not
 * present, nothing happens.
 *
 * Nothing here is written as markup. The dog's name is customer input and is
 * only ever assigned through textContent.
 */
(function () {
  'use strict';

  var nodes = document.querySelectorAll('[data-dog-state]');
  if (!nodes.length || !window.DogProfile) return;

  /**
   * "Refine for Alfie" narrows this collection by the dog's own facts — size and
   * life stage. Deliberately not the merchandising routes: this is a plain
   * filtered collection link whose chips the customer can see and remove, not a
   * curated shortlist, and it must not imply otherwise. If a fact does not apply
   * to this collection Shopify simply ignores it, and no chip appears to claim
   * a narrowing that did not happen.
   */
  function refineUrl(node, profile) {
    var base = node.getAttribute('data-collection-url');
    var sizeParam = node.getAttribute('data-param-size');
    var lifeParam = node.getAttribute('data-param-life');
    if (!base || (!sizeParam && !lifeParam)) return '';

    var params = new URLSearchParams();
    if (sizeParam && profile.size) params.append(sizeParam, profile.size);
    if (lifeParam && profile.life_stage) params.append(lifeParam, profile.life_stage);

    var query = params.toString();
    return query ? base + '?' + query : '';
  }

  /**
   * Add parameters to the guided URL without assuming what is already on it —
   * during development it carries ?view=, and at launch it will not.
   */
  function guidedWith(node, pairs) {
    var base = node.getAttribute('data-guided-url');
    if (!base) return '';
    var parts = base.split('?');
    var params = new URLSearchParams(parts[1] || '');
    Object.keys(pairs).forEach(function (key) {
      if (pairs[key]) params.set(key, pairs[key]);
    });
    var query = params.toString();
    return parts[0] + (query ? '?' + query : '');
  }

  /**
   * "Continue shopping" hands the dog's own answers back to the guided journey
   * so it resolves straight to their shortlist, rather than dropping them on
   * step one to re-answer questions the shop already remembers.
   */
  function continueUrl(node, profile) {
    return guidedWith(node, {
      dog: profile.name,
      size: profile.size,
      life: profile.life_stage,
      chars: (profile.characteristics || []).join(','),
      goal: profile.goal
    });
  }

  /**
   * "Change dog" must mean a fresh start. Without this the guided page would see
   * a saved dog and offer to resume it — making this control indistinguishable
   * from Continue shopping.
   */
  function changeUrl(node) {
    return guidedWith(node, { change: '1' });
  }

  /**
   * Fill a control's label from its own templates. The named template is only
   * used when there really is a name — a profile may be complete without one,
   * because naming the dog was always optional.
   */
  function label(el, name) {
    if (!el) return;
    var named = el.getAttribute('data-label-named');
    var plain = el.getAttribute('data-label-plain');
    // textContent, never innerHTML: the name is customer input.
    el.textContent = name && named ? named.split('@@').join(name) : (plain || '');
  }

  function ariaLabel(el, name) {
    if (!el) return;
    var named = el.getAttribute('data-aria-named');
    var plain = el.getAttribute('data-aria-plain');
    var value = name && named ? named.split('@@').join(name) : plain;
    if (value) el.setAttribute('aria-label', value);
  }

  /**
   * A saved pack outranks a saved dog. When a resumable pack is present and a
   * pack-state surface exists to carry it, the card/entry saved-dog states stand
   * down, so the page shows one saved surface rather than two competing cards.
   * The prompt variant (retail collections) is coordinated separately and is not
   * suppressed here.
   */
  function packOutranks(node) {
    var variant = node.getAttribute('data-variant');
    if (variant !== 'card' && variant !== 'entry') return false;
    if (!document.querySelector('[data-pack-state]')) return false;
    if (!window.PackDraft) return false;
    // An everyday pack outranks the saved dog.
    if (window.PackDraft.isResumable(window.PackDraft.read())) return true;
    // A puppy pack does too — but only where its saved state will actually reveal.
    // The homepage names data-require-match, so the dog card stands down only for
    // a matching puppy; without that gate any resumable puppy pack would.
    var puppyNode = document.querySelector('[data-pack-state][data-journey="puppy"]');
    if (puppyNode) {
      var Puppy = window.PackDraft.createStore({ key: 'dog_owned_puppy_pack_v1' });
      var pd = Puppy.read();
      if (Puppy.isResumable(pd)) {
        if (!puppyNode.hasAttribute('data-require-match')) return true;
        var profile = window.DogProfile && window.DogProfile.read();
        if (profile && Puppy.matchesProfile(pd, profile)) return true;
      }
    }
    return false;
  }

  function render(node, profile) {
    var invite = node.querySelector('[data-dog-invite]');
    var saved = node.querySelector('[data-dog-saved]');
    var isPrompt = node.getAttribute('data-variant') === 'prompt';

    if (packOutranks(node)) {
      if (invite) invite.hidden = true;
      if (saved) saved.hidden = true;
      node.hidden = true;
      return;
    }

    // A dog is worth surfacing once they have been shopped for — that is what a
    // goal means. The name is optional and never was the test: a completed
    // profile with no name still deserves "Continue shopping".
    var complete = !!(profile && profile.goal);
    if (!complete) {
      if (invite) invite.hidden = false;
      if (saved) saved.hidden = true;
      node.hidden = !isPrompt;
      return;
    }

    var name = profile.name || '';

    // With no name there is no "Shopping for ..." to write, so the line says
    // plainly what happened instead of trailing off.
    var nameEl = node.querySelector('[data-dog-name]');
    var eyebrow = node.querySelector('[data-dog-eyebrow]');
    var plain = node.querySelector('[data-dog-plain]');
    if (nameEl) { nameEl.textContent = name; nameEl.hidden = !name; }
    if (eyebrow) eyebrow.hidden = !name;
    if (plain) plain.hidden = !!name;

    var cont = node.querySelector('[data-dog-continue]');
    if (cont) {
      cont.setAttribute('href', continueUrl(node, profile));
      label(cont, name);
    }

    var refine = node.querySelector('[data-dog-refine]');
    if (refine) {
      var url = refineUrl(node, profile);
      // With nothing to refine by, sending them to the guided journey is honest;
      // a "Refine" that changed nothing would be a dead control.
      refine.setAttribute('href', url || node.getAttribute('data-guided-url'));
      label(refine, name);
    }

    var change = node.querySelector('[data-dog-change]');
    if (change) change.setAttribute('href', changeUrl(node));

    ariaLabel(node.querySelector('[data-dog-forget]'), name);

    if (invite) invite.hidden = true;
    if (saved) saved.hidden = false;
    node.hidden = false;
  }

  function renderAll() {
    var profile = window.DogProfile.read();
    Array.prototype.forEach.call(nodes, function (node) { render(node, profile); });
  }

  Array.prototype.forEach.call(nodes, function (node) {
    node.addEventListener('click', function (event) {
      if (!event.target.closest('[data-dog-forget]')) return;
      // Removes only dog_owned_profile_v1 — dog-profile.js guarantees the blast
      // radius, and unrelated localStorage belongs to the customer.
      window.DogProfile.forget();
      renderAll();
    });
  });

  // When a saved pack is cleared elsewhere on the page, a previously-deferred
  // saved-dog card can now take its turn.
  document.addEventListener('pack-state:changed', renderAll);

  renderAll();
})();
